import { useState, useEffect, useCallback, useMemo, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Checkbox } from '@/components/ui/checkbox';
import { useBulkSelection } from '@/hooks/useBulkSelection';
import { BulkActionToolbar } from '@/components/BulkActionToolbar';
import { Plus, Pencil, Trash2, MapPin, Users, Calendar, Upload, FileSpreadsheet, Loader2, Check, Receipt, Search, ChevronDown, ChevronRight, LayoutList, ListTree, AlertCircle } from 'lucide-react';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function CantieriPage() {
  const navigate = useNavigate();
  const [cantieri, setCantieri] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [clienti, setClienti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [editingCantiere, setEditingCantiere] = useState(null);
  const [selectedCantiere, setSelectedCantiere] = useState(null);
  const [selectedWorkers, setSelectedWorkers] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    description: '',
    status: 'attivo',
    start_date: '',
    end_date: '',
    cliente_id: '',
    client_name: '',
    prezzo_preventivato: 0
  });
  const [uploading, setUploading] = useState(false);
  const [autoLink, setAutoLink] = useState(true);
  const [importResult, setImportResult] = useState(null);

  const fetchCantieri = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/cantieri`, { withCredentials: true });
      setCantieri(response.data);
    } catch (error) {
      console.error('Errore caricamento cantieri:', error);
      toast.error('Errore nel caricamento cantieri');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchWorkers = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/users?role=operaio`, { withCredentials: true });
      setWorkers(response.data);
    } catch (error) {
      console.error('Error fetching workers:', error);
    }
  }, []);

  const fetchClienti = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/clienti`, { withCredentials: true });
      setClienti(response.data);
    } catch (error) {
      console.error('Errore caricamento clienti:', error);
    }
  }, []);

  useEffect(() => {
    fetchCantieri();
    fetchWorkers();
    fetchClienti();
  }, [fetchCantieri, fetchWorkers, fetchClienti]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingCantiere) {
        await axios.put(`${API}/cantieri/${editingCantiere.id}`, formData, { withCredentials: true });
        toast.success('Cantiere aggiornato');
      } else {
        await axios.post(`${API}/cantieri`, formData, { withCredentials: true });
        toast.success('Cantiere creato');
      }
      setDialogOpen(false);
      resetForm();
      fetchCantieri();
    } catch (error) {
      toast.error('Errore nel salvataggio');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo cantiere?')) return;
    try {
      await axios.delete(`${API}/cantieri/${id}`, { withCredentials: true });
      toast.success('Cantiere eliminato');
      fetchCantieri();
    } catch (error) {
      toast.error('Errore nell\'eliminazione');
    }
  };

  const handleImportFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setImportResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/cantieri/import?auto_link=${autoLink}`, fd, { withCredentials: true });
      setImportResult(res.data);
      toast.success(`Importati ${res.data.imported} cantieri${autoLink ? ` (${res.data.linked} collegati a clienti)` : ''}`);
      fetchCantieri();
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore importazione'); }
    finally { setUploading(false); e.target.value = ''; }
  };

  const handleAssignWorkers = async () => {
    try {
      await axios.post(`${API}/cantieri/${selectedCantiere.id}/assign-workers`, selectedWorkers, { withCredentials: true });
      toast.success('Operai assegnati');
      setAssignDialogOpen(false);
      fetchCantieri();
    } catch (error) {
      toast.error('Errore nell\'assegnazione');
    }
  };

  const openEditDialog = (cantiere) => {
    setEditingCantiere(cantiere);
    setFormData({
      name: cantiere.name,
      address: cantiere.address,
      description: cantiere.description || '',
      status: cantiere.status,
      start_date: cantiere.start_date || '',
      end_date: cantiere.end_date || ''
    });
    setDialogOpen(true);
  };

  const openAssignDialog = (cantiere) => {
    setSelectedCantiere(cantiere);
    setSelectedWorkers(cantiere.assigned_workers || []);
    setAssignDialogOpen(true);
  };

  const resetForm = () => {
    setEditingCantiere(null);
    setFormData({ name: '', address: '', description: '', status: 'attivo', start_date: '', end_date: '' });
  };

  const statusColors = {
    attivo: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    sospeso: 'bg-amber-100 text-amber-800 border-amber-200',
    completato: 'bg-blue-100 text-blue-800 border-blue-200'
  };

  // Bulk selection (declared before early-return for hooks rules)
  const bulkSel = useBulkSelection(cantieri, (c) => c.id);

  // iter86-o: Ricerca + Raggruppamento per cliente ------------------------
  const [search, setSearch] = useState('');
  const [grouped, setGrouped] = useState(() => {
    try { return localStorage.getItem('cantieri.grouped') !== '0'; } catch { return true; }
  });
  const [collapsedClients, setCollapsedClients] = useState({});
  useEffect(() => { try { localStorage.setItem('cantieri.grouped', grouped ? '1' : '0'); } catch { /* ignore */ } }, [grouped]);
  const filteredCantieri = useMemo(() => {
    const q = String(search || '').trim().toLowerCase();
    if (!q) return cantieri;
    return cantieri.filter(c => {
      const s = (v) => String(v == null ? '' : v).toLowerCase();
      return s(c.name).includes(q) || s(c.client_name).includes(q) || s(c.address).includes(q) || s(c.status).includes(q);
    });
  }, [cantieri, search]);
  const gruppiCantieri = useMemo(() => {
    const map = new Map();
    for (const c of filteredCantieri) {
      const key = String(c.client_name || '').trim() || '— Senza cliente —';
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(c);
    }
    return Array.from(map.entries())
      .sort(([a], [b]) => a.localeCompare(b, 'it', { sensitivity: 'base' }))
      .map(([nome, items]) => ({ nome, items: items.sort((x, y) => String(x.name || '').localeCompare(String(y.name || ''), 'it', { sensitivity: 'base' })) }));
  }, [filteredCantieri]);
  const orfaniCount = useMemo(() => cantieri.filter(c => !String(c.client_name || '').trim() && !String(c.cliente_id || '').trim()).length, [cantieri]);
  const isOrfano = (c) => !String(c.client_name || '').trim() && !String(c.cliente_id || '').trim();
  const toggleClientCollapse = (nome) => setCollapsedClients(prev => ({ ...prev, [nome]: !prev[nome] }));
  // ---------------------------------------------------------------------

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div></div>;
  }

  return (
    <div className="space-y-6" data-testid="cantieri-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">Cantieri</h1>
          <p className="text-slate-500 mt-1">Gestisci tutti i cantieri</p>
        </div>
        <div className="flex items-center gap-2">
          {/* Import */}
          <div className="flex items-center gap-2">
            <label className="flex items-center gap-1.5 text-sm text-slate-600 cursor-pointer select-none">
              <input type="checkbox" checked={autoLink} onChange={e => setAutoLink(e.target.checked)} className="rounded border-slate-300" />
              Collega clienti
            </label>
            <label className="cursor-pointer">
              <input type="file" accept=".xls,.xlsx,.csv,.pdf" className="hidden" onChange={handleImportFile} data-testid="import-cantieri-input" />
              <div className="inline-flex items-center justify-center h-10 px-4 rounded-md border border-slate-200 bg-white text-slate-700 text-sm font-medium hover:bg-slate-50 transition-colors">
                {uploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
                {uploading ? 'Importazione...' : 'Importa'}
              </div>
            </label>
          </div>
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-slate-900 text-white hover:bg-slate-800" data-testid="add-cantiere-btn">
              <Plus className="h-4 w-4 mr-2" />
              Nuovo Cantiere
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle>{editingCantiere ? 'Modifica Cantiere' : 'Nuovo Cantiere'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Cliente (opzionale)</Label>
                <Select value={formData.cliente_id || "none"} onValueChange={(v) => {
                  if (v === "none") {
                    setFormData({...formData, cliente_id: '', client_name: ''});
                  } else {
                    const cl = clienti.find(c => c.id === v);
                    setFormData({...formData, cliente_id: v, client_name: cl?.ragione_sociale || ''});
                  }
                }}>
                  <SelectTrigger data-testid="cantiere-cliente-select"><SelectValue placeholder="Seleziona cliente" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Nessun cliente</SelectItem>
                    {clienti.map(c => <SelectItem key={c.id} value={c.id}>{c.ragione_sociale} {c.partita_iva ? `(${c.partita_iva})` : ''}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Nome Cantiere</Label>
                <Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} required data-testid="cantiere-name-input" />
              </div>
              <div className="space-y-2">
                <Label>Indirizzo</Label>
                <Input value={formData.address} onChange={(e) => setFormData({...formData, address: e.target.value})} required data-testid="cantiere-address-input" />
              </div>
              <div className="space-y-2">
                <Label>Descrizione</Label>
                <Textarea value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} data-testid="cantiere-description-input" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Data Inizio</Label>
                  <Input type="date" value={formData.start_date} onChange={(e) => setFormData({...formData, start_date: e.target.value})} data-testid="cantiere-start-date-input" />
                </div>
                <div className="space-y-2">
                  <Label>Data Fine</Label>
                  <Input type="date" value={formData.end_date} onChange={(e) => setFormData({...formData, end_date: e.target.value})} data-testid="cantiere-end-date-input" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Stato</Label>
                <Select value={formData.status} onValueChange={(v) => setFormData({...formData, status: v})}>
                  <SelectTrigger data-testid="cantiere-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="attivo">Attivo</SelectItem>
                    <SelectItem value="sospeso">Sospeso</SelectItem>
                    <SelectItem value="completato">Completato</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Prezzo Preventivato (facoltativo)</Label>
                <Input type="number" step="0.01" min="0" value={formData.prezzo_preventivato || ''} onChange={(e) => setFormData({...formData, prezzo_preventivato: e.target.value ? parseFloat(e.target.value) : 0})} placeholder="0.00" data-testid="cantiere-prezzo-preventivato" />
                <p className="text-xs text-slate-400">Se inserito, verrà mostrato nella contabilità del cantiere</p>
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Annulla</Button>
                <Button type="submit" className="bg-slate-900 text-white hover:bg-slate-800" data-testid="cantiere-submit-btn">
                  {editingCantiere ? 'Aggiorna' : 'Crea'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Import result banner */}
      {importResult && (
        <Card className="border-emerald-200 bg-emerald-50">
          <CardContent className="p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Check className="h-5 w-5 text-emerald-600" />
              <span className="text-sm text-emerald-800">
                <strong>{importResult.imported}</strong> cantieri importati
                {importResult.linked > 0 && <> — <strong>{importResult.linked}</strong> collegati automaticamente a clienti</>}
                {importResult.skipped > 0 && <> — <strong>{importResult.skipped}</strong> righe saltate</>}
              </span>
            </div>
            <Button variant="ghost" size="sm" onClick={() => setImportResult(null)} className="text-emerald-600">Chiudi</Button>
          </CardContent>
        </Card>
      )}

      {/* Cantieri Table */}
      <Card className="border-slate-200">
        <CardContent className="p-0">
          {/* iter86-o: toolbar ricerca + toggle raggruppamento */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-2 px-4 py-3 border-b border-slate-100 bg-slate-50/60">
            <div className="relative w-full md:max-w-sm">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Cerca cantiere, cliente, indirizzo…"
                className="pl-9"
                data-testid="cantieri-search-input"
              />
            </div>
            <div className="flex items-center gap-3 text-sm text-slate-600 whitespace-nowrap">
              <span className="text-xs">
                {filteredCantieri.length} cantieri · {gruppiCantieri.length} clienti
              </span>
              {orfaniCount > 0 && (
                <button
                  type="button"
                  onClick={() => setSearch('— Senza cliente —')}
                  className="inline-flex items-center gap-1 rounded-full bg-red-50 text-red-700 border border-red-200 px-2 py-0.5 text-[11px] font-semibold hover:bg-red-100 transition-colors"
                  title="Filtra i cantieri senza cliente collegato"
                  data-testid="cantieri-orfani-badge"
                >
                  <AlertCircle className="h-3 w-3" />
                  {orfaniCount} senza cliente
                </button>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => setGrouped(g => !g)}
                className="gap-1.5"
                data-testid="cantieri-toggle-group"
                title={grouped ? 'Passa a vista lista piatta' : 'Raggruppa per cliente'}
              >
                {grouped ? <ListTree className="h-4 w-4" /> : <LayoutList className="h-4 w-4" />}
                {grouped ? 'Raggruppato' : 'Lista piatta'}
              </Button>
            </div>
          </div>

          <BulkActionToolbar
            resource="cantieri"
            selectedIds={bulkSel.selectedIds}
            onClear={bulkSel.clear}
            onDeleted={fetchCantieri}
            itemLabel="cantiere"
          />
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50 border-slate-200">
                <TableHead className="w-10 pl-3">
                  <Checkbox
                    checked={bulkSel.isAllSelected}
                    onCheckedChange={bulkSel.toggleAll}
                    aria-label="Seleziona tutti"
                    data-testid="bulk-select-all-cantieri"
                  />
                </TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500">Nome</TableHead>
                {!grouped && <TableHead className="text-xs uppercase tracking-wider text-slate-500">Cliente</TableHead>}
                <TableHead className="text-xs uppercase tracking-wider text-slate-500">Indirizzo</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500">Stato</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500">Operai</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-right">Azioni</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredCantieri.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={grouped ? 6 : 7} className="text-center py-8 text-slate-500">
                    {search ? 'Nessun cantiere corrisponde alla ricerca' : 'Nessun cantiere trovato'}
                  </TableCell>
                </TableRow>
              ) : grouped ? (
                gruppiCantieri.map(g => {
                  const isCollapsed = collapsedClients[g.nome];
                  const gruppoOrfano = g.nome === '— Senza cliente —';
                  return (
                    <Fragment key={g.nome}>
                      <TableRow className={`${gruppoOrfano ? 'bg-red-50/70 hover:bg-red-100/60' : 'bg-slate-100/70 hover:bg-slate-100'} border-slate-200 cursor-pointer`} onClick={() => toggleClientCollapse(g.nome)} data-testid={`cantieri-group-${g.nome.slice(0,30)}`}>
                        <TableCell className="pl-3" onClick={(e) => e.stopPropagation()}>
                          <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => toggleClientCollapse(g.nome)} title={isCollapsed ? 'Espandi' : 'Comprimi'}>
                            {isCollapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                          </Button>
                        </TableCell>
                        <TableCell colSpan={5} className={`font-semibold py-2.5 ${gruppoOrfano ? 'text-red-700' : 'text-slate-800'}`}>
                          <div className="flex items-center gap-2">
                            {gruppoOrfano ? <AlertCircle className="h-4 w-4 text-red-500" /> : <Users className="h-4 w-4 text-slate-500" />}
                            <span>{g.nome}</span>
                            <Badge variant="outline" className={`ml-1 text-[10px] py-0.5 px-1.5 ${gruppoOrfano ? 'bg-red-100 text-red-700 border-red-200' : 'bg-white'}`}>{g.items.length} cant.</Badge>
                            {gruppoOrfano && <span className="text-[11px] font-normal text-red-600 ml-1">Sistema questi cantieri collegandoli a un cliente</span>}
                          </div>
                        </TableCell>
                      </TableRow>
                      {!isCollapsed && g.items.map((cantiere) => (
                        <TableRow key={cantiere.id} className="border-slate-100 hover:bg-slate-50" data-selected={bulkSel.isSelected(cantiere.id) ? 'true' : 'false'}>
                          <TableCell className="pl-6">
                            <Checkbox
                              checked={bulkSel.isSelected(cantiere.id)}
                              onCheckedChange={() => bulkSel.toggle(cantiere.id)}
                              aria-label={`Seleziona ${cantiere.name}`}
                              data-testid={`bulk-select-cantiere-${cantiere.id}`}
                            />
                          </TableCell>
                          <TableCell className="font-medium text-slate-900">{cantiere.name}</TableCell>
                          <TableCell className="text-slate-600">
                            <div className="flex items-center gap-1">
                              <MapPin className="h-3 w-3" />
                              {cantiere.address}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge className={statusColors[cantiere.status]} variant="outline">
                              {cantiere.status}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Button variant="ghost" size="sm" onClick={() => openAssignDialog(cantiere)} className="text-slate-600">
                              <Users className="h-4 w-4 mr-1" />
                              {cantiere.assigned_workers?.length || 0}
                            </Button>
                          </TableCell>
                          <TableCell className="text-right">
                            <Button variant="ghost" size="icon" onClick={() => navigate(`/admin/cantieri?tab=contabilita&cantiere=${cantiere.id}`)} className="text-blue-600 hover:text-blue-700" title="Apri contabilità" data-testid={`contabilita-cantiere-${cantiere.id}`}>
                              <Receipt className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="icon" onClick={() => openEditDialog(cantiere)} data-testid={`edit-cantiere-${cantiere.id}`}>
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="icon" onClick={() => handleDelete(cantiere.id)} className="text-red-600 hover:text-red-700" data-testid={`delete-cantiere-${cantiere.id}`}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </Fragment>
                  );
                })
              ) : (
                filteredCantieri.map((cantiere) => (
                  <TableRow key={cantiere.id} className="border-slate-100 hover:bg-slate-50" data-selected={bulkSel.isSelected(cantiere.id) ? 'true' : 'false'}>
                    <TableCell className="pl-3">
                      <Checkbox
                        checked={bulkSel.isSelected(cantiere.id)}
                        onCheckedChange={() => bulkSel.toggle(cantiere.id)}
                        aria-label={`Seleziona ${cantiere.name}`}
                        data-testid={`bulk-select-cantiere-${cantiere.id}`}
                      />
                    </TableCell>
                    <TableCell className="font-medium text-slate-900">{cantiere.name}</TableCell>
                    <TableCell className="text-sm">
                      {isOrfano(cantiere) ? (
                        <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200 gap-1" data-testid={`cantiere-orfano-badge-${cantiere.id}`}>
                          <AlertCircle className="h-3 w-3" />
                          Senza cliente
                        </Badge>
                      ) : (
                        <span className="text-slate-600">{cantiere.client_name || '-'}</span>
                      )}
                    </TableCell>
                    <TableCell className="text-slate-600">
                      <div className="flex items-center gap-1">
                        <MapPin className="h-3 w-3" />
                        {cantiere.address}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={statusColors[cantiere.status]} variant="outline">
                        {cantiere.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Button variant="ghost" size="sm" onClick={() => openAssignDialog(cantiere)} className="text-slate-600">
                        <Users className="h-4 w-4 mr-1" />
                        {cantiere.assigned_workers?.length || 0}
                      </Button>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="icon" onClick={() => navigate(`/admin/cantieri?tab=contabilita&cantiere=${cantiere.id}`)} className="text-blue-600 hover:text-blue-700" title="Apri contabilità" data-testid={`contabilita-cantiere-${cantiere.id}`}>
                        <Receipt className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => openEditDialog(cantiere)} data-testid={`edit-cantiere-${cantiere.id}`}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => handleDelete(cantiere.id)} className="text-red-600 hover:text-red-700" data-testid={`delete-cantiere-${cantiere.id}`}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Assign Workers Dialog */}
      <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assegna Operai a {selectedCantiere?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm text-slate-500">{selectedWorkers.length}/{workers.length} selezionati</span>
              <Button variant="outline" size="sm" data-testid="select-all-workers-btn" onClick={() => {
                if (selectedWorkers.length === workers.length) {
                  setSelectedWorkers([]);
                } else {
                  setSelectedWorkers(workers.map(w => w.id));
                }
              }}>
                {selectedWorkers.length === workers.length ? 'Deseleziona Tutti' : 'Seleziona Tutti'}
              </Button>
            </div>
            <div className="max-h-64 overflow-y-auto space-y-2">
              {workers.map((worker) => (
                <label key={worker.id} className="flex items-center gap-3 p-3 border border-slate-200 rounded-lg cursor-pointer hover:bg-slate-50">
                  <input
                    type="checkbox"
                    checked={selectedWorkers.includes(worker.id)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedWorkers([...selectedWorkers, worker.id]);
                      } else {
                        setSelectedWorkers(selectedWorkers.filter(id => id !== worker.id));
                      }
                    }}
                    className="rounded border-slate-300"
                  />
                  <div>
                    <p className="font-medium text-slate-900">{worker.name}</p>
                    <p className="text-sm text-slate-500">{worker.email}</p>
                  </div>
                </label>
              ))}
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setAssignDialogOpen(false)}>Annulla</Button>
              <Button className="bg-slate-900 text-white hover:bg-slate-800" onClick={handleAssignWorkers} data-testid="assign-workers-btn">
                Salva Assegnazioni
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
